import { useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/types';
import {
  PayerOrganization,
  useLazyPayersSettingsRetrieveQuery,
  usePayersCreateMutation,
  usePayerSettingsPartialUpdateMutation,
  usePayersPartialUpdateMutation,
  useTilledPaymentMethodCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { usePaymentMethodCaptureContext } from 'src/common/forms/PaymentMethodCaptureForm/PaymentMethodCaptureContext';
import { validateBillingAddress } from 'src/common/forms/PaymentMethodCaptureForm/PaymentMethodCaptureForm';
import {
  BillingAddress,
  SavePaymentMethodCallback,
} from 'src/common/forms/PaymentMethodCaptureForm/types';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { formatBillingServerError } from 'src/features/billing/helpers/formatBillingServerError';

import { FormValues } from './FormValues';
import { payerOrgToPatchedPayerOrgSettings } from './payerOrgToPatchedPayerOrgSettings';
import { PayerSpecificSettings } from './PayerSpecificSettings/PayerSpecificSettings';

interface PayersFormStep2Props {
  onClose: () => void;
  onCloseAfterSave: () => void;
  savePaymentMethod: SavePaymentMethodCallback;
  validatePaymentForm: () => Promise<boolean>;
}

export function PayersFormStep2({
  onClose,
  onCloseAfterSave,
  savePaymentMethod,
  validatePaymentForm,
}: PayersFormStep2Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { getValues } = useFormContext<FormValues>();
  const { selectedPayer } = useSelector((state: RootState) => state.payer);
  const [createPayer] = usePayersCreateMutation();
  const [updatePayer] = usePayersPartialUpdateMutation();

  const [getPayerSettings] = useLazyPayersSettingsRetrieveQuery();
  const [updatePayerSettings] = usePayerSettingsPartialUpdateMutation();

  const [savePaymentMethodCreate] = useTilledPaymentMethodCreateMutation();
  const dispatch = useAppDispatch();
  const { areFieldsEmpty } = usePaymentMethodCaptureContext();

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(undefined);
      const values = getValues();

      if (!values.payer || !values.payer.value) {
        delete values.payer;
      } else {
        // @ts-ignore - Pulling the ID off of the option
        values.payer = values.payer.value;
      }

      if (!values.default_trip_address) delete values.default_trip_address;
      if (!values.billing_address) delete values.billing_address;

      let payer;
      if (!selectedPayer?.id) {
        payer = await createPayer({
          payerOrganization: values as PayerOrganization,
        }).unwrap();
      } else {
        payer = await updatePayer({
          id: selectedPayer.id,
          patchedPayerOrganization: values as PayerOrganization,
        }).unwrap();
      }

      const currentPayerSettings = await getPayerSettings({
        payerOrganizationId: payer.id,
      }).unwrap();

      const payerSettings = payerOrgToPatchedPayerOrgSettings(values);

      await updatePayerSettings({
        id: currentPayerSettings.id || '',
        patchedPayerOrganizationSettings: payerSettings,
      }).unwrap();

      dispatch(
        show({
          title: 'Successfully updated payer',
          type: 'success',
        })
      );

      const billingAddress: Partial<BillingAddress> = {
        city: values.billing_details?.city,
        country: values.billing_details?.country,
        zip: values.billing_details?.zip,
        state: values.billing_details?.state,
        street: values.billing_details?.street,
        street2: values.billing_details?.street2,
      };

      const validBillingAddress = validateBillingAddress(billingAddress);

      const billingName =
        values.billing_details?.billingName ?? values.billing_name;
      const validPaymentForm =
        (await validatePaymentForm()) && billingName && validBillingAddress;

      // Skip saving payment method
      if (areFieldsEmpty()) {
        onCloseAfterSave();
        return;
      }
      // Payment method form is invalid
      else if (!validPaymentForm) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to save payment method',
            description: 'Payment details are invalid, or missing',
          })
        );
      }
      // Payment method form is valid, attempt to save
      else {
        try {
          const savedPaymentMethod = await savePaymentMethod({
            type: 'card',
            billing_details: {
              name: billingName,
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              address: validBillingAddress
                ? {
                    ...validBillingAddress,
                    zip: validBillingAddress.zip,
                  }
                : undefined,
            },
          });

          // Attach payment method to pax
          await savePaymentMethodCreate({
            tilledPaymentMethodInput: {
              payer_id: payer.id,
              payer_type: 'payer_organization',
              payment_method_id: savedPaymentMethod.id,
            },
          });
          dispatch(
            show({
              title: 'Saved payment method',
              type: 'success',
            })
          );
          onCloseAfterSave();
        } catch (e) {
          dispatch(
            show({
              title: 'Failed to save payment method',
              description: formatBillingServerError(e),
              type: 'error',
            })
          );
        }
      }
    } catch (err) {
      const errorMessage = formatServerError(err);
      setError(errorMessage);
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <div className="flex flex-col gap-3 py-4 pb-16">
        <div>
          <h2 className="text-xl">Payer Specific Settings</h2>
          <p>If not specified, this payer will use organization defaults</p>
          {error && (
            <div className="pt-2">
              <FormErrorMessage>{error}</FormErrorMessage>
            </div>
          )}
          <div className="py-2">
            <PayerSpecificSettings />
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4">
        <div className="flex justify-end gap-4">
          <Button
            dataTestId="step2-cancel"
            onClick={() => {
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit()}
            variant="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  );
}
